/**
 * Raster text for Chrome → ZDesigner thermal labels.
 * Prefer sans-serif + pure B/W (no gray AA blobs). Slash in codes is drawn thick on purpose.
 */

export interface LabelTextPngOptions {
  fontSizePx: number;
  fontFamily?: string;
  fontStyle?: 'normal' | 'italic';
  fontWeight?: 'normal' | 'bold';
  underline?: boolean;
  paddingX?: number;
  paddingY?: number;
  /** Internal supersampling. */
  scale?: number;
  /**
   * When true and text contains "/", draw a thick thermal-friendly slash
   * (fixes 542/01 reading as 542 01).
   */
  thickenSlash?: boolean;
}

const DEFAULT_FONT = 'Arial, Helvetica, sans-serif';

function setupFont(
  ctx: CanvasRenderingContext2D,
  options: Required<
    Pick<LabelTextPngOptions, 'fontSizePx' | 'fontFamily' | 'fontStyle' | 'fontWeight'>
  >,
): string {
  const font = `${options.fontStyle} ${options.fontWeight} ${options.fontSizePx}px ${options.fontFamily}`;
  ctx.font = font;
  return font;
}

/**
 * Render label text to PNG. Uses 1-bit-ish black on white; slash can be forced thicker.
 */
export function labelTextToPngDataUrl(text: string, options: LabelTextPngOptions): string {
  const value = text.trim();
  if (value === '') return '';

  const fontSizePx = options.fontSizePx;
  const fontFamily = options.fontFamily ?? DEFAULT_FONT;
  const fontStyle = options.fontStyle ?? 'normal';
  const fontWeight = options.fontWeight ?? 'bold';
  const underline = options.underline === true;
  const paddingX = options.paddingX ?? 1;
  const paddingY = options.paddingY ?? 1;
  const scale = options.scale ?? 4;
  const thickenSlash = options.thickenSlash === true && value.includes('/');

  const measure = document.createElement('canvas');
  const mctx = measure.getContext('2d');
  if (mctx == null) return '';

  setupFont(mctx, { fontSizePx, fontFamily, fontStyle, fontWeight });

  let contentWidth = 0;
  const slashGap = Math.max(2, Math.round(fontSizePx * 0.12));
  const slashThickness = Math.max(2, Math.round(fontSizePx * 0.16));

  if (thickenSlash) {
    const [left, right] = value.split('/', 2);
    contentWidth =
      mctx.measureText(left).width +
      slashGap +
      slashThickness +
      slashGap +
      mctx.measureText(right ?? '').width;
  } else {
    contentWidth = mctx.measureText(value).width;
  }

  const sample = mctx.measureText(thickenSlash ? value.replace('/', ' ') : value);
  const ascent = Math.ceil(sample.actualBoundingBoxAscent || fontSizePx * 0.8);
  const descent = Math.ceil(sample.actualBoundingBoxDescent || fontSizePx * 0.25);
  const textH = ascent + descent;

  const w = Math.max(1, Math.ceil(contentWidth) + paddingX * 2);
  const h = Math.max(1, textH + paddingY * 2 + (underline ? 3 : 0));

  const canvas = document.createElement('canvas');
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext('2d');
  if (ctx == null) return '';

  // Draw at high res; avoid browser smoothing into gray (thermal hates gray).
  ctx.imageSmoothingEnabled = false;
  ctx.scale(scale, scale);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  setupFont(ctx, { fontSizePx, fontFamily, fontStyle, fontWeight });
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'alphabetic';

  const baseline = paddingY + ascent;
  let cursorX = paddingX;

  if (thickenSlash) {
    const [left, right] = value.split('/', 2);
    ctx.fillText(left, cursorX, baseline);
    cursorX += ctx.measureText(left).width + slashGap;

    // Thick forward slash "/" (positive angle = top-right to bottom-left).
    const slashH = ascent + descent * 0.35;
    const top = baseline - ascent + 1;
    ctx.save();
    ctx.translate(cursorX + slashThickness / 2, top + slashH / 2);
    ctx.rotate(0.4); // ~+23° → looks like /
    ctx.fillRect(-slashThickness / 2, -slashH / 2, slashThickness, slashH);
    ctx.restore();
    cursorX += slashThickness + slashGap;

    ctx.fillText(right ?? '', cursorX, baseline);
    cursorX += ctx.measureText(right ?? '').width;
  } else {
    ctx.fillText(value, cursorX, baseline);
    cursorX += contentWidth;
  }

  if (underline) {
    const uy = baseline + Math.max(1, Math.round(descent * 0.4)) + 1;
    ctx.fillRect(paddingX, uy, Math.max(1, cursorX - paddingX), 2);
  }

  return canvas.toDataURL('image/png');
}
